import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HowItWorks = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const timelineRef = useRef(null);
  const lineRef = useRef(null);

  const steps = [
    {
      number: '01',
      title: 'Install the Extension',
      description: 'Add Commit Watch to Chrome or Edge in just one click from the Web Store.',
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15M9 12l3 3m0 0l3-3m-3 3V2.25" />
        </svg>
      ),
    },
    {
      number: '02',
      title: 'Connect Your Account',
      description: 'Authenticate with GitHub or GitLab using a secure personal access token. Takes 30 seconds.',
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
        </svg>
      ),
    },
    {
      number: '03',
      title: 'Choose Your Repos',
      description: 'Select which repositories you want to track. Star your favorites for priority alerts.',
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
      ),
    },
    {
      number: '04',
      title: 'Stay in the Loop',
      description: 'Receive instant notifications and see the red badge counter when commits land.',
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
      ),
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header animation
      gsap.fromTo(headerRef.current?.children || [],
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: headerRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Animated line drawing effect
      gsap.fromTo(lineRef.current,
        { scaleY: 0 },
        {
          scaleY: 1,
          duration: 1,
          ease: 'power2.inOut',
          scrollTrigger: {
            trigger: timelineRef.current,
            start: 'top 70%',
            end: 'bottom 30%',
            scrub: 1,
          },
        }
      );

      // Steps animation
      const stepElements = timelineRef.current?.querySelectorAll('.step-item') || [];
      stepElements.forEach((step, index) => {
        // Card animation - staggered delay based on index
        gsap.fromTo(step.querySelector('.step-card'),
          { 
            x: index % 2 === 0 ? -60 : 60, 
            opacity: 0,
            rotateY: index % 2 === 0 ? -10 : 10,
          },
          {
            x: 0,
            opacity: 1,
            rotateY: 0,
            duration: 0.8,
            delay: index * 0.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: step,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        );

        // Dot animation
        gsap.fromTo(step.querySelector('.step-dot'),
          { scale: 0 },
          {
            scale: 1,
            duration: 0.4,
            delay: index * 0.1,
            ease: 'back.out(1.7)',
            scrollTrigger: {
              trigger: step,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        );

        // Number counter animation
        const numberEl = step.querySelector('.step-number');
        if (numberEl) {
          gsap.fromTo(numberEl,
            { scale: 0.5, opacity: 0 },
            {
              scale: 1,
              opacity: 1,
              duration: 0.4,
              delay: index * 0.1 + 0.1,
              ease: 'back.out(1.5)',
              scrollTrigger: {
                trigger: step,
                start: 'top 85%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });

      // Parallax background
      gsap.to('.how-orb', {
        y: -150,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.5,
        },
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Magnetic effect for cards
  const handleCardHover = (e) => {
    const card = e.currentTarget;
    gsap.to(card, {
      scale: 1.03,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  const handleCardLeave = (e) => {
    const card = e.currentTarget;
    gsap.to(card, {
      scale: 1,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-20 overflow-hidden"
      id="how-it-works"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark-950 via-dark-900/30 to-dark-950" />
      
      {/* Decorative elements */}
      <div className="how-orb absolute right-0 top-1/4 w-[500px] h-[500px] bg-accent-500/5 rounded-full blur-[150px]" />
      <div className="how-orb absolute left-0 bottom-1/4 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <div ref={headerRef} className="text-center mb-12 sm:mb-16 md:mb-24">
          <span className="inline-block px-4 sm:px-5 py-2 mb-6 sm:mb-8 text-xs sm:text-sm font-semibold text-accent-400 bg-accent-500/10 rounded-full border border-accent-500/20 tracking-wide uppercase">
            How It Works
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            Up and Running in{' '}
            <span className="gradient-text">Minutes</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-dark-400 max-w-2xl mx-auto px-2">
            No complex setup. No configuration headaches. Just install and go.
          </p>
        </div>

        {/* Timeline */}
        <div ref={timelineRef} className="relative">
          {/* Vertical line */}
          <div className="absolute left-6 sm:left-8 md:left-1/2 top-0 bottom-0 w-px bg-dark-700 md:-translate-x-1/2">
            <div
              ref={lineRef}
              className="absolute inset-0 bg-gradient-to-b from-accent-500 via-blue-500 to-purple-500 origin-top"
            />
          </div>

          {/* Steps */}
          <div className="space-y-16 md:space-y-32">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`step-item relative flex items-center gap-8 md:gap-20 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline dot */}
                <div className="step-dot absolute left-6 sm:left-8 md:left-1/2 w-4 h-4 sm:w-5 sm:h-5 -translate-x-1/2 z-10">
                  <div className="w-full h-full bg-accent-500 rounded-full ring-2 sm:ring-4 ring-dark-950" />
                  <div className="absolute inset-0 bg-accent-500 rounded-full animate-ping opacity-30" />
                </div>

                {/* Content */}
                <div className={`flex-1 pl-16 sm:pl-20 md:pl-0 ${index % 2 === 0 ? 'md:pr-24 md:text-right' : 'md:pl-24'}`}>
                  <div
                    className="step-card group p-5 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl glass border border-white/5 hover:border-accent-500/30 transition-all duration-500"
                    onMouseEnter={handleCardHover}
                    onMouseLeave={handleCardLeave}
                  >
                    <div className={`flex items-center gap-3 sm:gap-5 mb-4 sm:mb-5 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                      <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-accent-500/20 to-blue-500/20 flex items-center justify-center text-accent-400 group-hover:scale-110 transition-transform duration-500 flex-shrink-0">
                        {step.icon}
                      </div>
                      <span className="step-number text-4xl sm:text-5xl md:text-6xl font-bold text-dark-800 group-hover:text-dark-700 transition-colors">
                        {step.number}
                      </span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3 group-hover:text-accent-400 transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-dark-400 text-base sm:text-lg leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Empty space for alternating layout */}
                <div className="hidden md:block flex-1" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
